import { OrganizationDepartmentRecord } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiSheet, useAppForm } from 'noddi-ui-web';
import { useEffect } from 'react';
import { z } from 'zod';
import { invalidateDepartmentQueries } from './invalidateDepartmentQueries';

const FORM_ID = 'edit-department-general-form';

const validationSchema = z.object({
  name: z.string().min(1, 'Required')
});

type FormValues = z.input<typeof validationSchema>;

type Props = {
  open: boolean;
  onClose: () => void;
  department: OrganizationDepartmentRecord;
  departmentId: number;
};

export const EditDepartmentGeneralSheet = ({ open, onClose, department, departmentId }: Props) => {
  const { mutateAsync: patchDepartment } = noddiAsync.usePatch({
    type: URLKeys.patchDepartment,
    queryConfig: {
      onSuccess: () => {
        invalidateDepartmentQueries(departmentId);
        onClose();
      }
    }
  });

  const getDefaultValues = (): FormValues => ({
    name: department.name
  });

  const form = useAppForm({
    defaultValues: getDefaultValues(),
    validators: {
      onSubmit: validationSchema
    },
    onSubmit: async ({ value }) => {
      await patchDepartment({
        departmentId,
        name: value.name.trim()
      });
    }
  });

  useEffect(() => {
    if (open) {
      form.reset(getDefaultValues());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getDefaultValues is a new closure each render and would reset on every render; reset only on open or when the fields it reads change
  }, [open, form, department.name]);

  return (
    <form.AppForm>
      <NoddiSheet
        open={open}
        onOpenChange={(next) => !next && onClose()}
        title='Edit general details'
        footer={<form.SubscribeButton formId={FORM_ID} label='Save' className='w-full' />}
      >
        <form
          id={FORM_ID}
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <div className='flex flex-col gap-5'>
            <form.AppField name='name'>{(field) => <field.Input label='Department name' required />}</form.AppField>
          </div>
        </form>
      </NoddiSheet>
    </form.AppForm>
  );
};
